import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import { ArrowLeft, Camera, RefreshCw, Check, CheckCircle, AlertCircle, XCircle, FileText, Calendar, User, Shield } from 'lucide-react';
import { useVerificationStore } from '../stores/verificationStore';
import api from '../services/api';
import toast from 'react-hot-toast';

const IDCapture = () => {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showCamera, setShowCamera] = useState(true);
  const [cameraReady, setCameraReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [uploading, setUploading] = useState(false);

  const { uploadEvidence, clientFileId } = useVerificationStore();

  const videoConstraints = {
    facingMode: 'environment',
    width: { ideal: 1920 },
    height: { ideal: 1080 }
  };

  const handleCapture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setCapturedImage(imageSrc);
      setShowCamera(false);
      setProcessing(true);
    }
  }, [webcamRef]);

  // Analyze document after capture using AI
  useEffect(() => {
    if (processing && capturedImage) {
      const validateDocument = async () => {
        try {
          const response = await api.post('/evidence/validate', {
            imageData: capturedImage,
            documentType: 'ID'
          });

          const data = response.data;

          if (!data.valid) {
            // Document rejected
            setExtractedData({
              documentType: data.documentType || 'Unknown',
              name: data.holderName || 'Not detected',
              documentNumber: data.documentNumber || null,
              expiryDate: data.expiryStatus || 'Unknown',
              isValid: false,
              rejectionMessage: data.message,
              quality: {
                overall: data.recommendation === 'reject' ? 'Invalid' : 'Poor',
                score: data.qualityScore || 0,
                issues: data.issues || []
              }
            });
            toast.error(data.message || 'Invalid document');
          } else {
            // Document accepted
            setExtractedData({
              documentType: data.documentType || 'Identity Document',
              name: data.holderName || 'Document holder detected',
              documentNumber: data.documentNumber || '•••••••••',
              expiryDate: data.expiryStatus || 'Valid',
              isValid: true,
              quality: {
                overall: data.qualityScore >= 80 ? 'Good' : data.qualityScore >= 50 ? 'Fair' : 'Poor',
                score: data.qualityScore || 0,
                corners: !data.issues?.includes('Corners not visible'),
                glare: data.issues?.includes('Glare detected'),
                blur: data.issues?.includes('Blurry')
              }
            });
          }

          setProcessing(false);
          setAnalysisComplete(true);
        } catch (error) {
          console.error('Document validation error:', error);
          toast.error('Failed to validate document. Please try again.');
          setProcessing(false);
          handleRetake();
        }
      };

      validateDocument();
    }
  }, [processing, capturedImage]);

  const handleRetake = () => {
    setCapturedImage(null);
    setShowCamera(true);
    setProcessing(false);
    setAnalysisComplete(false);
    setExtractedData(null);
  };

  const handleContinue = async () => {
    if (!clientFileId) {
      toast.error('Verification session not found. Please start again.');
      navigate('/');
      return;
    }

    setUploading(true);
    try {
      await uploadEvidence('ID_FRONT', capturedImage, extractedData || {});
      toast.success('ID document uploaded');
      navigate('/liveness');
    } catch (error) {
      console.error('Failed to upload ID:', error);
      toast.error('Failed to upload ID. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 safe-area-top">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 bg-black/50 rounded-full text-white"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="bg-black/50 rounded-full px-4 py-2">
            <span className="text-white text-sm font-medium">Step 1 of 3</span>
          </div>
          <div className="w-10" />
        </div>
      </div>

      {/* Camera / Preview / Confirmation */}
      <div className="flex-1 relative">
        {showCamera ? (
          <>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              onUserMedia={() => setCameraReady(true)}
              mirrored={false}
              className="w-full h-full object-cover"
            />

            {/* Document overlay */}
            <div className="document-overlay">
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-white text-sm font-medium bg-black/50 px-3 py-1 rounded-full">
                Position ID within frame
              </div>
            </div>

            {/* Capture button */}
            <div className="absolute bottom-8 left-0 right-0 flex justify-center safe-area-bottom">
              <button
                onClick={handleCapture}
                disabled={!cameraReady}
                className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg disabled:opacity-50"
              >
                <div className="w-16 h-16 border-4 border-navy-900 rounded-full" />
              </button>
            </div>
          </>
        ) : processing ? (
          /* Processing/Analyzing screen */
          <div className="w-full h-full bg-gray-900 flex flex-col items-center justify-center p-6">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mb-6"></div>
            <h3 className="text-white text-xl font-semibold mb-2">Analyzing Document</h3>
            <p className="text-gray-400 text-center">Please wait while we verify your ID...</p>
          </div>
        ) : analysisComplete && extractedData ? (
          /* Confirmation screen with extracted data */
          <div className="w-full h-full bg-white overflow-y-auto">
            {/* Header - Success or Error */}
            {extractedData.isValid ? (
              <div className="bg-green-50 p-6 text-center border-b border-green-100">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">ID Captured Successfully</h3>
                <p className="text-gray-600 mt-1">We've verified your document</p>
              </div>
            ) : (
              <div className="bg-red-50 p-6 text-center border-b border-red-100">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Invalid Document</h3>
                <p className="text-red-600 mt-1">{extractedData.rejectionMessage || 'This does not appear to be a valid ID'}</p>
              </div>
            )}

            {/* Document thumbnail */}
            <div className="p-4">
              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                <img
                  src={capturedImage}
                  alt="Captured ID"
                  className="w-full h-40 object-cover"
                />
              </div>
            </div>

            {/* Extracted data */}
            <div className="px-4 pb-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Document Details</h4>
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-blue-600 mr-3" />
                  <div>
                    <p className="text-xs text-gray-500">Document Type</p>
                    <p className="font-medium text-gray-900">{extractedData.documentType}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <User className="w-5 h-5 text-blue-600 mr-3" />
                  <div>
                    <p className="text-xs text-gray-500">Holder</p>
                    <p className="font-medium text-gray-900">{extractedData.name}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-blue-600 mr-3" />
                  <div>
                    <p className="text-xs text-gray-500">Status</p>
                    <p className="font-medium text-green-600">{extractedData.expiryDate}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quality indicators */}
            <div className="px-4 pb-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Image Quality</h4>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-700">Overall Quality</span>
                  <span className={`font-semibold ${extractedData.quality.score >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                    {extractedData.quality.overall} ({extractedData.quality.score}%)
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">All corners visible</span>
                    {extractedData.quality.corners ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">No glare detected</span>
                    {!extractedData.quality.glare ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Image is sharp</span>
                    {!extractedData.quality.blur ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="p-4 space-y-3">
              {extractedData.isValid ? (
                <button
                  onClick={handleContinue}
                  disabled={uploading}
                  className="w-full btn-primary disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Continue to Selfie
                    </>
                  )}
                </button>
              ) : null}
              <button
                onClick={handleRetake}
                disabled={uploading}
                className={`w-full ${extractedData.isValid ? 'btn-secondary' : 'btn-primary'} disabled:opacity-50`}
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                {extractedData.isValid ? 'Retake Photo' : 'Try Again'}
              </button>
            </div>
          </div>
        ) : (
          /* Fallback preview */
          <>
            <img
              src={capturedImage}
              alt="Captured ID"
              className="w-full h-full object-contain bg-black"
            />
          </>
        )}
      </div>

      {/* Instructions - only show when camera is active */}
      {showCamera && (
        <div className="bg-white p-4 safe-area-bottom">
          <h3 className="font-medium text-gray-900 mb-2">Photo ID Required</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Passport or driving licence</li>
            <li>• Ensure all corners are visible</li>
            <li>• Avoid glare and shadows</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default IDCapture;
